<template>
  <div class="flex flex-col w-full h-full overflow-y-auto px-4 pb-8 gap-4">
    <!-- Header: image + overall score -->
    <div class="flex items-center gap-4 pt-2">
      <img
        :src="scan.imageUrl"
        alt="skin scan"
        class="w-20 h-20 rounded-xl object-cover border border-gray-200 flex-none"
      />
      <div class="flex flex-col gap-1 flex-1 min-w-0">
        <span class="text-xs text-gray-500">{{ formatScanDateTime(scan.createdAt) }}</span>
        <div class="flex items-center gap-2 flex-wrap">
          <span v-if="scan.skinType" class="text-xs bg-primary-50 text-primary px-2 py-0.5 rounded-full">
            סוג עור: {{ scan.skinType }}
          </span>
          <span v-if="scan.estimatedSkinAge" class="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
            גיל עור משוער: {{ scan.estimatedSkinAge }}
          </span>
        </div>
      </div>
      <!-- Overall score ring -->
      <div class="relative w-16 h-16 flex-none">
        <svg viewBox="0 0 36 36" class="w-16 h-16 -rotate-90">
          <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#e5e7eb" stroke-width="3" />
          <circle
            cx="18" cy="18" r="15.9155" fill="none"
            :stroke="scoreColor(scan.overallScore)" stroke-width="3" stroke-linecap="round"
            :stroke-dasharray="`${scan.overallScore}, 100`"
          />
        </svg>
        <div class="absolute inset-0 flex flex-col items-center justify-center">
          <span class="text-base font-bold leading-none">{{ scan.overallScore }}</span>
          <span class="text-[9px] text-gray-400 leading-none">ציון כללי</span>
        </div>
      </div>
    </div>

    <!-- Image gallery (front/left/right) — only when more than one angle was captured -->
    <div v-if="images.length > 1" class="flex gap-2 overflow-x-auto -mx-1 px-1">
      <div v-for="img in images" :key="img.url" class="flex flex-col items-center gap-1 flex-none">
        <img :src="img.url" alt="" class="w-20 h-24 rounded-lg object-cover border border-gray-200" />
        <span class="text-[10px] text-gray-500">{{ directionLabels[img.direction] }}</span>
      </div>
    </div>

    <!-- Summary -->
    <p v-if="scan.summary" class="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-lg p-3">
      {{ scan.summary }}
    </p>

    <!-- Owner note (annotates the progress trend) -->
    <div class="rounded-lg border border-amber-100 bg-amber-50/40 p-3 flex flex-col gap-2">
      <div class="flex items-center justify-between">
        <span class="text-xs font-semibold text-amber-700 flex items-center gap-1">
          <i class="pi pi-flag-fill" style="font-size: 0.6rem" /> הערה לטיפול
        </span>
        <button v-if="!editingNote" type="button" class="text-xs text-primary" @click="startEditNote">
          {{ localNotes ? 'עריכה' : 'הוספה' }}
        </button>
      </div>
      <p v-if="!editingNote && localNotes" class="text-sm text-gray-700">{{ localNotes }}</p>
      <p v-else-if="!editingNote" class="text-xs text-gray-400">
        הוסיפו הערה — למשל "התחילה סרום חדש" — והיא תופיע על גרף ההתקדמות כדי להבין מה השפיע.
      </p>
      <div v-else class="flex flex-col gap-2">
        <textarea
          v-model="noteDraft"
          rows="2"
          maxlength="280"
          placeholder='למשל: התחילה סרום ויטמין C'
          class="w-full text-sm border border-gray-200 rounded-lg p-2 resize-none focus:outline-none focus:border-primary"
        />
        <div class="flex gap-2 justify-end">
          <button type="button" class="text-xs text-gray-500 px-3 py-1" @click="cancelEditNote">ביטול</button>
          <button
            type="button"
            class="text-xs text-white bg-primary rounded-full px-3 py-1 disabled:opacity-60"
            :disabled="savingNote"
            @click="saveNote"
          >{{ savingNote ? 'שומר...' : 'שמירה' }}</button>
        </div>
      </div>
    </div>

    <!-- Concerns -->
    <div class="flex flex-col gap-2">
      <h3 class="text-sm font-semibold text-gray-800">מדדי עור</h3>
      <div
        v-for="c in scan.concerns"
        :key="c.concern"
        class="flex flex-col gap-1 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
        @click="toggle(c.concern)"
      >
        <div class="flex items-center justify-between gap-2">
          <span class="text-sm text-gray-700">{{ c.label }}</span>
          <span class="text-sm font-semibold tabular-nums" :style="{ color: scoreColor(c.score) }">{{ c.score }}</span>
        </div>
        <div class="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
          <div class="h-full rounded-full transition-all" :style="{ width: c.score + '%', backgroundColor: scoreColor(c.score) }" />
        </div>
        <p v-if="expanded === c.concern && c.description" class="text-xs text-gray-500 pt-1">
          {{ c.description }}
        </p>
      </div>
    </div>

    <!-- Disclaimer -->
    <p class="text-[11px] text-gray-400 leading-relaxed border-t border-gray-100 pt-3">
      * סריקת העור היא הערכה אסתטית בלבד המבוססת על בינה מלאכותית, ואינה מהווה אבחנה או ייעוץ רפואי.
      בכל חשש בריאותי יש לפנות לרופא/ה.
    </p>

    <!-- Delete -->
    <button
      type="button"
      class="flex items-center justify-center gap-2 text-sm text-red-600 py-2"
      @click="onDelete"
    >
      <i class="pi pi-trash text-xs" />
      <span>מחיקת סריקה</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, type PropType } from 'vue';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'vue-toastification';
import type { ISkinScan, SkinConcernEnum } from '@torim-app/types';
import { skinImageDirectionLabels } from '@torim-app/types';
import { SkinScanApi } from '@app/api/admin';
import { scoreColor, formatScanDateTime, scanImages } from './skin-scan.utils';

const props = defineProps({
  scan: {
    type: Object as PropType<ISkinScan>,
    required: true,
  },
});
const emit = defineEmits(['deleted', 'updated', 'close']);

const confirm = useConfirm();
const toast = useToast();
const expanded = ref<SkinConcernEnum | null>(null);

// All captured images (front/left/right), with back-compat for legacy single-image scans.
const images = computed(() => scanImages(props.scan));
const directionLabels = skinImageDirectionLabels;

// Owner note (shown as an annotation on the progress trend)
const localNotes = ref(props.scan.notes || '');
const editingNote = ref(false);
const noteDraft = ref('');
const savingNote = ref(false);

const startEditNote = () => {
  noteDraft.value = localNotes.value;
  editingNote.value = true;
};
const cancelEditNote = () => {
  editingNote.value = false;
};
const saveNote = async () => {
  savingNote.value = true;
  try {
    const updated = await SkinScanApi.updateNote(String(props.scan._id), noteDraft.value.trim());
    localNotes.value = updated.notes || '';
    editingNote.value = false;
    emit('updated', updated);
    toast.success('ההערה נשמרה');
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לשמור את ההערה');
  } finally {
    savingNote.value = false;
  }
};

const toggle = (concern: SkinConcernEnum) => {
  expanded.value = expanded.value === concern ? null : concern;
};

const onDelete = () => {
  confirm.require({
    message: 'למחוק את הסריקה? פעולה זו אינה ניתנת לשחזור.',
    header: 'מחיקת סריקה',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'מחיקה',
    rejectLabel: 'ביטול',
    accept: async () => {
      try {
        await SkinScanApi.deleteScan(String(props.scan._id));
        toast.success('הסריקה נמחקה');
        emit('deleted', props.scan._id);
        emit('close');
      } catch (e) {
        console.error(e);
        toast.error('לא הצלחנו למחוק את הסריקה');
      }
    },
  });
};
</script>
